import type { ReadonlyEventHost } from '@amazon/vinyl-util'
import { EventHostImpl } from '@amazon/vinyl-util'

/**
 * An Event indicating an element in a list has been modified.
 */
export interface ElementChangedEvent<T> {
    readonly index: number
    readonly previous: T | null
    readonly current: T
}

/**
 * An Event indicating an index range has been invalidated.
 */
export interface IndexRangeInvalidateEvent {
    readonly startIndex: number
    readonly endIndex: number
}

export interface ListControllerEventMap<E> {
    /**
     * Dispatched when an element has been modified.
     */
    readonly elementChanged: ElementChangedEvent<E>

    /**
     * Dispatched when an index range has been invalidated.
     */
    readonly invalidated: IndexRangeInvalidateEvent
}

export interface ListController<E> extends ReadonlyEventHost<ListControllerEventMap<E>> {
    /**
     * Invalidates all renderers.
     */
    invalidate(): void

    /**
     * Invalidates the renderer at the given index.
     * This will cause new data to be requested from the provider.
     *
     * @param index The index to invalidate.
     */
    // eslint-disable-next-line @typescript-eslint/unified-signatures
    invalidate(index: number): void

    /**
     * Invalidates the renderer at the given index range.
     * This will cause new data to be requested from the provider.
     *
     * @param startIndex The starting index to invalidate.
     * @param endIndex The end index to invalidate (exclusive).
     */
    // eslint-disable-next-line @typescript-eslint/unified-signatures
    invalidate(startIndex: number, endIndex: number): void
}

export class ListControllerImpl<E>
    extends EventHostImpl<ListControllerEventMap<E>>
    implements ListController<E>
{
    invalidate(startIndex?: number, endIndex?: number): void {
        this.dispatch('invalidated', {
            startIndex: startIndex ?? 0,
            endIndex:
                endIndex == null
                    ? startIndex == null
                        ? Number.MAX_SAFE_INTEGER
                        : startIndex + 1
                    : endIndex,
        })
    }

    /**
     * Notifies that an element has changed.
     * @param event
     */
    notifyChanged(event: ElementChangedEvent<E>): void {
        this.dispatch('elementChanged', event)
    }
}
